import { useState, useEffect, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ColumnFilterPopover } from '../c360/ColumnFilterPopover';
import { Pagination } from './Pagination';
import './RelationSelect.css';

const PAGE_SIZE_OPTIONS = [5, 10, 15];

/**
 * İlişki (relation) alanları için zengin seçim bileşeni.
 * Kapalı kontrol açılınca bir arama kutusuna dönüşür (combobox); açılan panelde
 * ilişkinin en önemli kolonlarını gösteren bir datagrid bulunur. Genel arama
 * kontrolün kendisinde, kolon-bazlı filtre c360 listesindeki ile birebir aynı
 * (ColumnFilterPopover) huni simgesinden, sıralama başlığa tıklanarak yapılır.
 *
 * İki çalışma modu:
 *   - İstemci modu (rows / fetchRows): tüm liste bir kez çekilir, filtre/sort/sayfa
 *     bellekte. Orta boy ilişkiler (NamedDefinition gibi) için ideal.
 *   - Sunucu modu (fetchPage): her sayfa sunucudan çekilir; arama/filtre/sort/sayfa
 *     sunucuya iletilir. Büyük listeler (Müşteri/Ürün) için. fetchPage STABİL olmalı
 *     (useCallback/useMemo), aksi halde sürekli yeniden sorgular.
 *
 * Props:
 *   value, selectedLabel, onChange(value,row)
 *   columns: [{ key, header, type?, width?, align?, filterable?, sortable?, render? }]
 *   fetchRows()  : Promise<row[]>                — istemci modu (cache'lenir)
 *   rows         : row[]                          — istemci modu (doğrudan veri)
 *   fetchPage({ search, columnFilters, sort, page, pageSize }) : Promise<{ rows, total }> — sunucu modu
 *   getRowId, getRowLabel, searchKeys
 *   placeholder, searchPlaceholder, disabled, allowClear, title
 */
export function RelationSelect({
  value,
  selectedLabel,
  onChange,
  columns = [],
  fetchRows,
  rows: rowsProp,
  fetchPage,
  getRowId = (row) => row.id,
  getRowLabel,
  searchKeys,
  placeholder = 'Seçiniz',
  searchPlaceholder = 'Ara…',
  disabled = false,
  allowClear = true,
  title,
  // closeOnSelect=false: çoklu seçimde (MultiChipSelect) panel seçim sonrası açık kalır.
  closeOnSelect = true,
  // selectedValues: çoklu modda zaten eklenmiş değerler — panelde işaretlenir.
  selectedValues,
  // #247 Panel en az bu genişlikte açılsın (dar hücrelerdeki picker'lar için, ör. ürün seç).
  panelMinWidth = 0,
  // #248 Yeniden açılınca arama kutusuna seçili kaydın kodu ön-dolsun (kullanıcı düzenleyebilsin).
  selectedCode,
  // #262 Dışarıdan imperatif açma sinyali: bu sayaç her değiştiğinde picker açılır ve
  // arama kutusu odaklanır (ör. fatura kalemi eklendikten sonra imleci bir sonraki
  // ürün-ekleme alanına taşımak için). 0/undefined ise hiçbir şey yapmaz.
  openToken,
}) {
  const serverMode = typeof fetchPage === 'function';

  const selectedSet = useMemo(
    () => new Set((selectedValues || []).map((v) => String(v))),
    [selectedValues],
  );

  const [open, setOpen] = useState(false);
  const [allRows, setAllRows] = useState(rowsProp || []);
  const [loaded, setLoaded] = useState(Array.isArray(rowsProp));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sunucu modu sayfa verisi
  const [serverRows, setServerRows] = useState([]);
  const [serverTotal, setServerTotal] = useState(0);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // colFilters: { [colKey]: { op, value, value2 } } — c360 filtre modeli
  const [colFilters, setColFilters] = useState({});
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const [pageSize, setPageSize] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [highlight, setHighlight] = useState(-1);
  const [labelCache, setLabelCache] = useState({});
  const [pos, setPos] = useState({ top: 0, left: 0, width: 620, maxHeight: 460 });

  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const bodyRef = useRef(null);
  const reqRef = useRef(0);

  const rowLabel = useCallback((row) => {
    if (!row) return '';
    if (getRowLabel) return getRowLabel(row);
    const first = columns[0];
    return first ? String(row[first.key] ?? '') : String(getRowId(row) ?? '');
  }, [getRowLabel, columns, getRowId]);

  // rowsProp dışarıdan güncellenirse senkronla (istemci modu)
  useEffect(() => {
    if (Array.isArray(rowsProp)) { setAllRows(rowsProp); setLoaded(true); }
  }, [rowsProp]);

  // Seçili etiketi cache'le (düzenleme modunda satırlar yüklenmeden görünsün)
  useEffect(() => {
    if (value && selectedLabel) {
      setLabelCache((c) => (c[value] ? c : { ...c, [value]: selectedLabel }));
    }
  }, [value, selectedLabel]);

  // Yüklenen/görünen satırlardan seçili etiketi tamamla
  useEffect(() => {
    if (!value) return;
    const pool = serverMode ? serverRows : allRows;
    const row = pool.find((r) => String(getRowId(r)) === String(value));
    if (row) setLabelCache((c) => ({ ...c, [value]: rowLabel(row) }));
  }, [value, serverMode, serverRows, allRows, getRowId, rowLabel]);

  const displayLabel = value ? (labelCache[value] || selectedLabel || 'Seçili kayıt') : '';

  // İstemci modu: ilk açılışta tüm veriyi çek (fetchRows)
  const loadRows = useCallback(async () => {
    if (serverMode || loaded || !fetchRows) return;
    const reqId = reqRef.current + 1;
    reqRef.current = reqId;
    setLoading(true);
    setError(null);
    try {
      const data = (await fetchRows()) || [];
      if (reqRef.current === reqId) { setAllRows(data); setLoaded(true); }
    } catch (e) {
      if (reqRef.current === reqId) setError(e?.message || 'Veri alınamadı');
    } finally {
      if (reqRef.current === reqId) setLoading(false);
    }
  }, [serverMode, loaded, fetchRows]);

  const effectiveSearchKeys = useMemo(
    () => searchKeys || columns.map((c) => c.key),
    [searchKeys, columns],
  );

  const cellText = useCallback((row, col) => {
    const raw = row[col.key];
    if (raw == null) return '';
    if (col.type === 'date') {
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString('tr-TR');
    }
    return String(raw);
  }, []);

  // Tek bir kolon filtresini (c360 {op,value,value2}) istemci tarafında uygula
  const matchFilter = useCallback((row, key, filter, type) => {
    if (!filter || !filter.op) return true;
    const op = filter.op;
    const raw = row[key];
    const blank = raw == null || String(raw).trim() === '';
    if (op === 'blank') return blank;
    if (op === 'notBlank') return !blank;
    if (type === 'number') {
      const n = Number(raw); const v = Number(filter.value); const v2 = Number(filter.value2);
      switch (op) {
        case 'equals': return n === v;
        case 'notEquals': return n !== v;
        case 'gt': return n > v;
        case 'gte': return n >= v;
        case 'lt': return n < v;
        case 'lte': return n <= v;
        case 'between': return n >= v && n <= v2;
        default: return true;
      }
    }
    if (type === 'date') {
      const d = new Date(raw).getTime();
      const v = new Date(filter.value).getTime();
      const v2 = new Date(filter.value2).getTime();
      switch (op) {
        case 'equals': return new Date(raw).toDateString() === new Date(filter.value).toDateString();
        case 'before': return d < v;
        case 'after': return d > v;
        case 'between': return d >= v && d <= v2;
        default: return true;
      }
    }
    const c = String(raw ?? '').toLowerCase();
    const v = String(filter.value ?? '').toLowerCase();
    switch (op) {
      case 'contains': return c.includes(v);
      case 'notContains': return !c.includes(v);
      case 'equals': return c === v;
      case 'notEquals': return c !== v;
      case 'startsWith': return c.startsWith(v);
      case 'endsWith': return c.endsWith(v);
      default: return true;
    }
  }, []);

  // İstemci modu görünür satırlar: genel arama → kolon filtreleri → sıralama
  const viewRows = useMemo(() => {
    if (serverMode) return [];
    const q = search.trim().toLowerCase();
    let out = allRows;
    if (q) {
      out = out.filter((row) => effectiveSearchKeys.some((k) => String(row[k] ?? '').toLowerCase().includes(q)));
    }
    const fEntries = Object.entries(colFilters);
    if (fEntries.length) {
      out = out.filter((row) => fEntries.every(([k, f]) => matchFilter(row, k, f, columns.find((c) => c.key === k)?.type || 'text')));
    }
    if (sort.key) {
      const col = columns.find((c) => c.key === sort.key);
      const dir = sort.dir === 'desc' ? -1 : 1;
      out = [...out].sort((a, b) => {
        const av = a[sort.key];
        const bv = b[sort.key];
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * dir;
        if (bv == null) return 1 * dir;
        if (col?.type === 'number') return (Number(av) - Number(bv)) * dir;
        if (col?.type === 'date') return (new Date(av).getTime() - new Date(bv).getTime()) * dir;
        return String(av).localeCompare(String(bv), 'tr') * dir;
      });
    }
    return out;
  }, [serverMode, allRows, search, colFilters, sort, columns, effectiveSearchKeys, matchFilter]);

  // Filtre/arama/sıralama değişince ilk sayfaya dön
  useEffect(() => { setCurrentPage(1); }, [search, colFilters, sort]);

  // Genel arama debounce (sunucu modu için)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Sunucu modu: sayfa verisini çek
  useEffect(() => {
    if (!open || !serverMode) return undefined;
    let cancelled = false;
    const reqId = reqRef.current + 1;
    reqRef.current = reqId;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetchPage({
          search: debouncedSearch.trim(),
          columnFilters: colFilters,
          sort: sort.key ? sort : null,
          page: currentPage,
          pageSize,
        });
        if (cancelled || reqRef.current !== reqId) return;
        setServerRows(res?.rows || []);
        setServerTotal(Number.isFinite(res?.total) ? res.total : (res?.rows?.length || 0));
      } catch (e) {
        if (!cancelled && reqRef.current === reqId) { setError(e?.message || 'Veri alınamadı'); setServerRows([]); setServerTotal(0); }
      } finally {
        if (!cancelled && reqRef.current === reqId) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, serverMode, fetchPage, debouncedSearch, colFilters, sort, currentPage, pageSize]);

  // İstemci modu sayfalama
  const clientTotalPages = Math.max(1, Math.ceil(viewRows.length / pageSize));
  const clientSafePage = Math.min(currentPage, clientTotalPages);
  const clientPageRows = useMemo(
    () => viewRows.slice((clientSafePage - 1) * pageSize, clientSafePage * pageSize),
    [viewRows, clientSafePage, pageSize],
  );

  // Render edilecek satırlar / sayfalama girdileri (mod'a göre)
  const rowsToRender = serverMode ? serverRows : clientPageRows;
  const totalCount = serverMode ? serverTotal : viewRows.length;
  const pageForPagination = serverMode ? currentPage : clientSafePage;
  const paginationVisible = !error && totalCount > 0 && (serverMode || loaded);

  // Portal konumlandırma — panel genişliği = kontrol (input) genişliği (en az panelMinWidth).
  const computePos = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 8;
    // #247 Dar hücrelerde (ör. ürün seç td'si) panel okunmaz kadar dar açılıyordu; en az
    // panelMinWidth kadar aç, viewport'a sığdır ve gerekirse sola kaydır.
    const width = Math.min(Math.max(r.width, panelMinWidth || 0), vw - margin * 2);
    let left = r.left;
    if (left + width > vw - margin) left = Math.max(margin, vw - margin - width);
    const desired = 460;
    const spaceBelow = vh - r.bottom - margin;
    const spaceAbove = r.top - margin;
    if (spaceBelow < Math.min(desired, 280) && spaceAbove > spaceBelow) {
      // Yukarı aç: paneli ALT kenarından input'un hemen üstüne sabitle. (top + maxHeight
      // kullanırsak içerik kısa olunca panel yukarıda asılı kalıp input'tan kopuyordu.)
      const maxHeight = Math.max(220, Math.min(desired, spaceAbove));
      setPos({ top: undefined, bottom: vh - r.top + 4, left, width, maxHeight });
    } else {
      const maxHeight = Math.max(220, Math.min(desired, spaceBelow));
      setPos({ top: r.bottom + 4, bottom: undefined, left, width, maxHeight });
    }
  }, [panelMinWidth]);

  useLayoutEffect(() => { if (open) computePos(); }, [open, computePos]);

  // Dışarı tık → paneli kapat; scroll → KAPATMA, anchor'a göre yeniden konumlandır (#244).
  useEffect(() => {
    if (!open) return undefined;
    const mainPortal = () => document.getElementById('ui-rs-portal-active');
    const inFilterPop = (el) => el?.closest?.('.c360-filter-popover');
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (mainPortal()?.contains(e.target)) return;
      if (inFilterPop(e.target)) return;
      setOpen(false);
    };
    const onScroll = (e) => {
      // resize event'inde e.target = window (Node değil); contains() patlamasın diye koru.
      const t = e.target;
      if (t instanceof Node) {
        if (mainPortal()?.contains(t)) return; // panel içi scroll → aramayı bozma
        if (inFilterPop(t)) return;
      }
      // #244/#247 Sayfa kaydırılınca paneli KAPATMA (arama/filtre uçmasın); sadece taşı.
      computePos();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, computePos]);

  const openPicker = () => {
    if (disabled) return;
    // #248 Seçili kayıt varsa arama kutusuna kodunu ön-doldur (kullanıcı harf değiştirip
    // aramayı düzenleyebilsin — sıfırdan yazmak zorunda kalmasın). Yoksa boş başlat.
    const seed = value && selectedCode ? String(selectedCode) : '';
    setOpen(true);
    setSearch(seed);
    setDebouncedSearch(seed);
    setCurrentPage(1);
    setHighlight(-1);
    loadRows();
    setTimeout(() => { searchRef.current?.focus(); searchRef.current?.select?.(); }, 0);
  };
  // #243 Kontrole tekrar tıklayınca aç/kapa (toggle). Eskiden openPicker aramayı '' yapıp
  // aynı değerle effect'i tetiklemediği için liste boş kalıyordu; toggle bunu önler.
  const toggleOpen = () => { if (disabled) return; if (open) setOpen(false); else openPicker(); };

  // #262 openToken sayacı arttığında picker'ı imperatif olarak aç (arama kutusuna odaklan).
  // openPicker her render'da yeniden üretildiğinden ref üzerinden çağırıyoruz ki effect
  // yalnız token değişince tetiklensin (her render'da değil).
  const openPickerRef = useRef(null);
  openPickerRef.current = openPicker;
  const prevOpenTokenRef = useRef(openToken);
  useEffect(() => {
    if (openToken && openToken !== prevOpenTokenRef.current) {
      prevOpenTokenRef.current = openToken;
      if (!disabled) openPickerRef.current?.();
    }
  }, [openToken, disabled]);

  const pick = (row) => {
    const id = getRowId(row);
    setLabelCache((c) => ({ ...c, [id]: rowLabel(row) }));
    onChange(id, row);
    if (closeOnSelect) {
      setOpen(false);
    } else {
      // Çoklu seçim: panel açık kalsın; arama/filtre/sayfa AYNEN korunsun ki kullanıcı
      // aynı sayfadan (örn. 2. sayfa) art arda ürün ekleyebilsin. Sadece odağı koru.
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  };

  const clear = () => {
    onChange('', null);
    setOpen(false);
  };

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setSort((s) => {
      if (s.key !== col.key) return { key: col.key, dir: 'asc' };
      if (s.dir === 'asc') return { key: col.key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  };

  const applyColFilter = (key, next) => {
    setColFilters((f) => ({ ...f, [key]: next }));
    setHighlight(-1);
  };
  const clearColFilter = (key) => {
    setColFilters((f) => { const n = { ...f }; delete n[key]; return n; });
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, rowsToRender.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (rowsToRender[highlight]) pick(rowsToRender[highlight]); }
  };

  // Highlight değişince görünür satırı kaydır
  useEffect(() => {
    if (highlight < 0 || !bodyRef.current) return;
    const el = bodyRef.current.querySelector(`[data-rowidx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const activeFilterCount = Object.keys(colFilters).length;

  return (
    <div className="ui-rs" ref={wrapRef}>
      <div
        className={`ui-rs-control${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`}
        onClick={toggleOpen}
        role="combobox"
        aria-haspopup="dialog"
        aria-controls="ui-rs-portal-active"
        aria-expanded={open}
      >
        {open ? (
          <input
            ref={searchRef}
            className="ui-rs-control-input"
            value={search}
            placeholder={displayLabel || searchPlaceholder}
            onChange={(e) => { setSearch(e.target.value); setHighlight(-1); }}
            onKeyDown={onKeyDown}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span className={`ui-rs-value${displayLabel ? '' : ' is-placeholder'}`}>
            {displayLabel || placeholder}
          </span>
        )}
        {!disabled && value && allowClear && !open && (
          <span
            className="ui-rs-clear"
            role="button"
            title="Temizle"
            onClick={(e) => { e.stopPropagation(); clear(); }}
          >
            ×
          </span>
        )}
        <span className="ui-rs-caret" aria-hidden="true">▾</span>
      </div>

      {open && createPortal(
        <div
          id="ui-rs-portal-active"
          className="ui-rs-pop"
          style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          onKeyDown={onKeyDown}
        >
          {title && (
            <div className="ui-rs-head">
              <div className="ui-rs-title">{title}</div>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  className="ui-rs-resetbtn"
                  onClick={() => { setColFilters({}); setHighlight(-1); }}
                  title="Kolon filtrelerini temizle"
                >
                  Filtreleri temizle ({activeFilterCount})
                </button>
              )}
            </div>
          )}

          <div className="ui-rs-gridwrap">
            <table className="ui-rs-grid">
              <thead>
                <tr className="ui-rs-headrow">
                  {columns.map((col) => {
                    const sorted = sort.key === col.key ? sort.dir : null;
                    return (
                      <th
                        key={col.key}
                        style={{ width: col.width, textAlign: col.align || 'left' }}
                        className={col.sortable === false ? undefined : 'is-sortable'}
                        onClick={() => toggleSort(col)}
                      >
                        <span className="ui-rs-th-inner">
                          <span className="ui-rs-th-label">{col.header}</span>
                          <span className="ui-rs-sorticon">{sorted === 'asc' ? '▲' : sorted === 'desc' ? '▼' : ''}</span>
                          {col.filterable !== false && (
                            <span onClick={(e) => e.stopPropagation()}>
                              <ColumnFilterPopover
                                column={{ id: col.key, columnDef: { header: col.header } }}
                                filter={colFilters[col.key]}
                                filterType={col.type || 'text'}
                                onApply={(next) => applyColFilter(col.key, next)}
                                onClear={() => clearColFilter(col.key)}
                              />
                            </span>
                          )}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody ref={bodyRef}>
                {allowClear && value && !search.trim() && (
                  <tr className="ui-rs-clearrow" onClick={clear}>
                    <td colSpan={columns.length}>✕ Seçimi kaldır</td>
                  </tr>
                )}
                {loading && (
                  <tr className="ui-rs-msgrow"><td colSpan={columns.length}>Yükleniyor…</td></tr>
                )}
                {!loading && error && (
                  <tr className="ui-rs-msgrow is-error"><td colSpan={columns.length}>{error}</td></tr>
                )}
                {!loading && !error && rowsToRender.length === 0 && (
                  <tr className="ui-rs-msgrow"><td colSpan={columns.length}>Kayıt bulunamadı</td></tr>
                )}
                {!loading && !error && rowsToRender.map((row, i) => {
                  const id = getRowId(row);
                  const isSel = String(id) === String(value) || selectedSet.has(String(id));
                  return (
                    <tr
                      key={id}
                      data-rowidx={i}
                      className={`ui-rs-row${i === highlight ? ' is-active' : ''}${isSel ? ' is-selected' : ''}`}
                      onMouseEnter={() => setHighlight(i)}
                      onClick={() => pick(row)}
                    >
                      {columns.map((col, ci) => (
                        <td key={col.key} style={{ textAlign: col.align || 'left' }}>
                          {ci === 0 && !closeOnSelect && selectedSet.has(String(id)) && (
                            <span className="ui-rs-check" aria-hidden="true">✓ </span>
                          )}
                          {col.render ? col.render(row) : cellText(row, col)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {paginationVisible && (
            <div className="ui-rs-foot">
              <Pagination
                currentPage={pageForPagination}
                totalCount={totalCount}
                pageSize={pageSize}
                rowCount={rowsToRender.length}
                onPageChange={(p) => { setCurrentPage(p); setHighlight(-1); }}
                onPageSizeChange={(sz) => { setPageSize(sz); setCurrentPage(1); setHighlight(-1); }}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
              />
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
